// ABOUTME: LangGraph chat API client for thread management and message streaming
// ABOUTME: Communicates with Python FastAPI backend with Neo4j integration

import { LangChainMessage, LangGraphMessagesEvent } from "@assistant-ui/react-langgraph";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('access_token');
  if (!token) return {};
  return { 'Authorization': `Bearer ${token}` };
};

export const createThread = async () => {
  const response = await fetch(`${API_BASE_URL}/api/threads`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to create thread: ${response.statusText}`);
  }

  return response.json();
};

export const getThreadState = async (threadId: string) => {
  const response = await fetch(`${API_BASE_URL}/api/threads/${threadId}/state`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to get thread state: ${response.statusText}`);
  }

  return response.json();
};

export const listThreads = async () => {
  const response = await fetch(`${API_BASE_URL}/api/threads`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to list threads: ${response.statusText}`);
  }

  return response.json();
};

export async function* sendMessage(params: {
  threadId: string;
  messages: LangChainMessage[];
}): AsyncGenerator<LangGraphMessagesEvent<LangChainMessage>> {
  const requestBody = {
    input: { messages: params.messages },
    config: {
      configurable: {
        thread_id: params.threadId,
      },
    },
    stream_mode: "messages",
  };

  const response = await fetch(`${API_BASE_URL}/api/runs/stream`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${await response.text()}`);
  }

  if (!response.body) {
    throw new Error("No response body");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          const data = line.slice(5).trim();
          if (data) {
            try {
              const parsed = JSON.parse(data);

              // Yield in LangGraph format
              yield {
                event: currentEvent || "messages/partial",
                data: parsed,
              } as LangGraphMessagesEvent<LangChainMessage>;
            } catch (e) {
              console.error("Failed to parse SSE data:", data, e);
            }
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
